import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import QRScanner from './components/QRScanner';
import ManualInput from './components/ManualInput';
import VerificationProcessor from './components/VerificationProcessor';
import VerificationResults from './components/VerificationResults';
import VerificationHistory from './components/VerificationHistory';
import BatchVerification from './components/BatchVerification';

const CredentialVerification = () => {
  const [activeTab, setActiveTab] = useState('scanner');
  const [verificationState, setVerificationState] = useState('input'); // input, processing, results
  const [currentCredential, setCurrentCredential] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [isScannerActive, setIsScannerActive] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const tabs = [
    {
      id: 'scanner',
      label: 'QR Scanner',
      icon: 'QrCode',
      description: 'Scan QR codes with camera'
    },
    {
      id: 'manual',
      label: 'Manual Input',
      icon: 'FileText',
      description: 'Enter credential data manually'
    },
    {
      id: 'batch',
      label: 'Batch Verification',
      icon: 'Layers',
      description: 'Verify multiple credentials'
    },
    {
      id: 'history',
      label: 'History',
      icon: 'History',
      description: 'View past verifications'
    }
  ];

  const addNotification = (message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date()?.toISOString()
    };
    setNotifications(prev => [notification, ...prev?.slice(0, 4)]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev?.filter(n => n?.id !== notification?.id));
    }, 5000);
  };

  const handleScanResult = (scanData) => {
    setCurrentCredential(scanData);
    setVerificationState('processing');
    setIsScannerActive(false);
    addNotification('QR code scanned successfully. Starting verification...', 'success');
  };

  const handleManualSubmit = (credentialData) => {
    setCurrentCredential(credentialData);
    setVerificationState('processing');
    addNotification('Credential submitted. Starting verification...', 'success');
  };

  const handleVerificationComplete = (result) => {
    setVerificationResult(result);
    setVerificationState('results');
    
    const statusMessage = result?.overallStatus === 'verified' ?'Credential verification completed successfully!'
      : result?.overallStatus === 'warning' ?'Credential verification completed with warnings.' :'Credential verification failed.';
    
    addNotification(statusMessage, result?.overallStatus === 'verified' ? 'success' : 
                   result?.overallStatus === 'warning' ? 'warning' : 'error');
  };

  const handleVerificationCancel = () => {
    setVerificationState('input');
    setCurrentCredential(null);
    addNotification('Verification cancelled.', 'info');
  };

  const handleVerifyAnother = () => {
    setVerificationState('input');
    setCurrentCredential(null);
    setVerificationResult(null);
    setActiveTab('scanner');
  };

  const handleExportReport = (reportData) => {
    addNotification('Verification report exported successfully.', 'success');
  };

  const handleSelectHistoryItem = (historyItem) => {
    // Simulate loading historical verification result
    const mockResult = {
      credentialId: historyItem?.credentialId,
      verificationId: historyItem?.id,
      timestamp: historyItem?.timestamp,
      overallStatus: historyItem?.status,
      confidence: historyItem?.confidence,
      stepResults: {
        format_validation: { status: 'passed', timestamp: historyItem?.timestamp },
        signature_verification: { status: 'passed', timestamp: historyItem?.timestamp },
        issuer_verification: { status: 'passed', timestamp: historyItem?.timestamp },
        blockchain_anchoring: { status: 'passed', timestamp: historyItem?.timestamp },
        revocation_check: { status: 'passed', timestamp: historyItem?.timestamp },
        expiration_check: { status: historyItem?.status === 'warning' ? 'failed' : 'passed', timestamp: historyItem?.timestamp },
        zk_proof_validation: { status: 'passed', timestamp: historyItem?.timestamp }
      },
      summary: {
        totalSteps: historyItem?.stepsTotal,
        passedSteps: historyItem?.stepsPassed,
        failedSteps: historyItem?.stepsTotal - historyItem?.stepsPassed,
        processingTime: historyItem?.processingTime
      },
      credential: {
        id: historyItem?.credentialId,
        type: historyItem?.credentialType,
        issuer: historyItem?.issuer,
        subject: historyItem?.subject
      },
      inputMethod: historyItem?.inputMethod
    };

    setVerificationResult(mockResult);
    setVerificationState('results');
    addNotification('Historical verification loaded.', 'info');
  };

  const handleBatchProcess = (batchResults) => {
    const successCount = batchResults?.filter(r => r?.status === 'verified')?.length;
    const totalCount = batchResults?.length;
    addNotification(`Batch verification completed: ${successCount}/${totalCount} credentials verified.`, 'success');
  };

  useEffect(() => {
    // Cleanup scanner when component unmounts or tab changes
    if (activeTab !== 'scanner') {
      setIsScannerActive(false);
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Credential Verification</h1>
                <p className="text-muted-foreground mt-2">
                  Verify the authenticity and validity of digital credentials using advanced cryptographic methods
                </p>
              </div>
              
              {/* Quick Stats */}
              <div className="hidden lg:flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-success">98.5%</div>
                  <div className="text-sm text-muted-foreground">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">2.3s</div>
                  <div className="text-sm text-muted-foreground">Avg. Time</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">1,247</div>
                  <div className="text-sm text-muted-foreground">Verified Today</div>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications */}
          {notifications?.length > 0 && (
            <div className="fixed top-20 right-4 z-50 space-y-2">
              {notifications?.map((notification) => (
                <div
                  key={notification?.id}
                  className={`max-w-sm p-4 rounded-lg shadow-lg border animate-in slide-in-from-right ${
                    notification?.type === 'success' ? 'bg-success/10 border-success/20 text-success' :
                    notification?.type === 'warning' ? 'bg-warning/10 border-warning/20 text-warning' :
                    notification?.type === 'error'? 'bg-destructive/10 border-destructive/20 text-destructive' : 'bg-primary/10 border-primary/20 text-primary'
                  }`}
                >
                  <div className="flex items-center">
                    <Icon 
                      name={
                        notification?.type === 'success' ? 'CheckCircle' :
                        notification?.type === 'warning' ? 'AlertTriangle' :
                        notification?.type === 'error'? 'XCircle' : 'Info'
                      } 
                      size={16} 
                      className="mr-2 flex-shrink-0" 
                    />
                    <span className="text-sm font-medium">{notification?.message}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {verificationState === 'input' && (
            <>
              {/* Navigation Tabs */}
              <div className="mb-8">
                <div className="border-b border-border">
                  <nav className="-mb-px flex space-x-8 overflow-x-auto">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                          activeTab === tab?.id
                            ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                        }`}
                      >
                        <Icon name={tab?.icon} size={20} className="mr-2" />
                        <div className="text-left">
                          <div>{tab?.label}</div>
                          <div className="text-xs text-muted-foreground hidden sm:block">
                            {tab?.description}
                          </div>
                        </div>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Tab Content */}
              <div className="space-y-8">
                {activeTab === 'scanner' && (
                  <QRScanner
                    onScanResult={handleScanResult}
                    isActive={isScannerActive}
                    onToggle={setIsScannerActive}
                  />
                )}

                {activeTab === 'manual' && (
                  <ManualInput onSubmitCredential={handleManualSubmit} />
                )}

                {activeTab === 'batch' && (
                  <BatchVerification onBatchProcess={handleBatchProcess} />
                )}

                {activeTab === 'history' && (
                  <VerificationHistory onSelectVerification={handleSelectHistoryItem} />
                )}
              </div>
            </>
          )}

          {verificationState === 'processing' && (
            <div className="max-w-4xl mx-auto">
              <VerificationProcessor
                credential={currentCredential}
                onComplete={handleVerificationComplete}
                onCancel={handleVerificationCancel}
              />
            </div>
          )}

          {verificationState === 'results' && (
            <div className="max-w-6xl mx-auto">
              <VerificationResults
                result={verificationResult}
                onVerifyAnother={handleVerifyAnother}
                onExportReport={handleExportReport}
              />
            </div>
          )}

          {/* Help Section */}
          {verificationState === 'input' && (
            <div className="mt-12 bg-muted/30 border border-border rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Icon name="HelpCircle" size={24} className="text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-foreground mb-3">How Credential Verification Works</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
                    <div>
                      <h4 className="font-medium text-foreground mb-2">1. Input Credential</h4>
                      <p className="text-muted-foreground">
                        Scan QR codes, paste JSON data, or enter credential URLs for verification
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-2">2. Cryptographic Validation</h4>
                      <p className="text-muted-foreground">
                        Verify digital signatures, check blockchain anchoring, and validate proofs
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-2">3. Issuer Verification</h4>
                      <p className="text-muted-foreground">
                        Confirm issuer identity, check revocation status, and validate authority
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-2">4. Results & Report</h4>
                      <p className="text-muted-foreground">
                        Get detailed verification results with confidence scores and audit trails
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CredentialVerification;